import React, { useState } from 'react'
import { useMutation, useQuery } from 'react-apollo'
import { pathEq } from 'ramda'

import {
  TVOD_PURCHASE_RESTRICTION
} from '../../../../constants'
import { getGQLErrorMsg } from '../../../../lib/apollo'
import { CardWrapper, CardContent, CardInnerHeading } from '../../../shared/settings/card'
import Toggle from '../../../shared/settings/toggle'

import ACCOUNT_QUERY from '../../../../../graphql/queries/account.gql'
import ACCOUNT_MUTATION from '../../../../../graphql/mutations/account.gql'
import {
  PURCHASING_HEADER,
  PURCHASING_SUBHEADER,
  PURCHASING_PASSWORD_TEXT,
  PURCHASING_PASSWORD_SUBTEXT,
  PURCHASING_PASSWORD_TOOLTIP,
  PURCHASING_PIN_TEXT,
  PURCHASING_PIN_SUBTEXT,
  PURCHASING_PIN_TOOLTIP
} from '../constants'

import styles from './purchasing.css'

export default function Purchasing() {
  const [error, setError] = useState('')
  const {
    data: accountData,
    loading: accountLoading,
    error: accountError
  } = useQuery(ACCOUNT_QUERY)
  const [updateAccount] = useMutation(ACCOUNT_MUTATION)

  const account = accountData?.account

  if (accountLoading || accountError || !account || !account.settings) {
    return null
  }

  const hasNoPin = pathEq(['hasPin'], false, account)
  const pinIsOn = pathEq(
    ['settings', 'tvodPurchaseRestriction'], TVOD_PURCHASE_RESTRICTION.PIN, account
  )
  const passwordIsOn = pathEq(
    ['settings', 'tvodPurchaseRestriction'], TVOD_PURCHASE_RESTRICTION.PASSWORD, account
  )

  const changePurchaseRestriction = (value) => {
    updateAccount({
      variables: {
        input: {
          settings: {
            tvodPurchaseRestriction: value
          }
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        account: {
          __typename: 'Account',
          ...account,
          settings: {
            __typename: 'AccountSettings',
            ...account.settings,
            tvodPurchaseRestriction: value
          }
        }
      },
      update: (proxy, response) => {
        const data = proxy.readQuery({ query: ACCOUNT_QUERY })
        proxy.writeQuery({
          query: ACCOUNT_QUERY,
          data: {
            ...data,
            account: {
              ...data.account,
              settings: {
                ...data.account.settings,
                tvodPurchaseRestriction: response.data.account.settings.tvodPurchaseRestriction
              }
            }
          }
        })
      }
    })
      .catch((mutationError) => {
        setError(getGQLErrorMsg(mutationError))
      })
  }

  const toggleTVODRestriction = () => {
    const value = pinIsOn
      ? TVOD_PURCHASE_RESTRICTION.PASSWORD
      : TVOD_PURCHASE_RESTRICTION.PIN
    changePurchaseRestriction(value)
  }

  return (
    <CardWrapper>
      <CardContent className={styles.cardContent}>
        <CardInnerHeading
          header={PURCHASING_HEADER}
          subHeader={PURCHASING_SUBHEADER}
        />
        <Toggle
          data-e2e="purchasing-security-restriction-password"
          wrapperClassName={styles.passwordProtection}
          textWrapperClassName={styles.textWrapperClass}
          containerClassName={styles.toggleTextClass}
          text={PURCHASING_PASSWORD_TEXT}
          subText={PURCHASING_PASSWORD_SUBTEXT}
          toggleText={passwordIsOn ? 'On' : 'Off'}
          checked={passwordIsOn}
          onChange={toggleTVODRestriction}
          tooltipText={hasNoPin ? PURCHASING_PASSWORD_TOOLTIP : ''}
          disabled={hasNoPin}
        />

        <Toggle
          data-e2e="purchasing-security-restriction-PIN"
          wrapperClassName={styles.toggleWrapperClass}
          textWrapperClassName={styles.textWrapperClass}
          containerClassName={styles.toggleTextClass}
          text={PURCHASING_PIN_TEXT}
          subText={PURCHASING_PIN_SUBTEXT}
          toggleText={pinIsOn ? 'On' : 'Off'}
          checked={pinIsOn}
          onChange={toggleTVODRestriction}
          tooltipText={hasNoPin ? PURCHASING_PIN_TOOLTIP : ''}
          disabled={hasNoPin}
        />

        <div className={styles.warningMessage}>{error}</div>
      </CardContent>
    </CardWrapper>
  )
}
